<script>
  import Layout from '../components/Layout.svelte';
  import SendEsitoModal from '../components/SendEsitoModal.svelte';
  import SignInvoiceModal from '../components/SignInvoiceModal.svelte';
  import SignatureVerificationCard from '../components/SignatureVerificationCard.svelte';
  import InvoiceCorrectionForm from '../components/InvoiceCorrectionForm.svelte';
  import { tick, onDestroy } from 'svelte';
  import { companyInvoicesApi, companyTranchesApi } from '../stores/api.js';
  import { activeCompany, activeCompanyId, companiesLoading } from '../stores/company.js';
  import { isAdmin } from '../stores/roles.js';
  import {
    canWriteInCompany, capabilities, capabilitiesStatus, refreshCapabilities,
  } from '../stores/capabilities.js';
  import { anticipoView, featureAccess } from '../lib/features.js';
  import FeatureBadge from '../components/FeatureBadge.svelte';
  import { push, link } from 'svelte-spa-router';
  import { t, tImmediate } from '../i18n/index.js';
  import { ArrowLeft, Send, RefreshCw, Clock, CheckCircle, XCircle, Plus, Trash2, Download, Pencil, Save, X as XIcon, FileSignature, ShieldCheck, Building2, FileEdit, FileText, Wallet, MailOpen, Info, AlertTriangle } from '../lib/icons/index.js';
  import StatusBadge from '../components/StatusBadge.svelte';
  import DirectionBadge from '../components/DirectionBadge.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import {
    statusLabel, directionLabel, environmentLabel, formatoLabel,
    notificationTypeLabel, notificationSdiCode, localizedError,
  } from '../lib/labels.js';
  import { todayCalendarDay } from '../lib/payment.js';
  import { isAnticipoFatturaEligible } from '../lib/anticipo-fattura.js';
  import {
    canDownloadCourtesyPdf, courtesyPdfStatusCopy, courtesyXmlNoticeKey,
  } from '../lib/courtesy-pdf.js';
  import RecordPaymentDialog from '../components/RecordPaymentDialog.svelte';
  import CourtesyEmailPanel from '../components/CourtesyEmailPanel.svelte';
  import { refreshUnread } from '../stores/unread.js';
  import { canMarkUnread, markUnreadAndReturn, stillShowing } from '../lib/read-state.js';
  import {
    isPaymentOpen, paymentBadge, METODI_EFFETTIVI, metodoEffettivoLabel, instalmentShownPaid, instalmentActions,
    showsInstalments,
  } from '../lib/payment-status.js';

  // Transport diagnostics (the transmissions) are admin-only; everyone else
  // gets the documents, payments, history and SdI notifications.

  function notificationType(n) {
    return n.notificationType || n.type || '';
  }

  /** The anticipo is computed on demand, the first time its section opens. */
  function onAnticipoToggle(event) {
    if (event.currentTarget.open) loadAnticipoFattura();
  }

  export let params = {};

  let invoice = null;
  let signature = null;
  let showSignModal = false;
  let history = [];
  let transmissions = [];
  let notifications = [];
  let transitions = null;
  let tranches = [];
  let loading = true;
  let error = null;
  let showEsitoModal = false;

  let payingOpen = false;

  async function onPaymentRecorded() {
    payingOpen = false;
    // Silent: the summary and the instalments change, the page stays where it is.
    await loadData({ silent: true });
  }

  $: pagamento = invoice?.pagamento ?? null;

  /** The other party: the customer of an issued invoice, the supplier of a received one. */
  $: counterparty = (() => {
    const party = invoice?.metadata?.[invoice?.direction === 'inbound' ? 'cedente' : 'cessionario'];
    if (party?.denominazione) return party.denominazione;
    const full = [party?.nome, party?.cognome].filter(Boolean).join(' ').trim();
    if (full) return full;
    return (invoice?.direction === 'inbound' ? invoice?.senderTaxId : invoice?.receiverTaxId) || '';
  })();
  $: pagamentoBadge = pagamento ? paymentBadge(pagamento, invoice?.direction) : null;
  let retryLoading = false;
  let anticipoData = null;
  let anticipoLoading = false;
  let anticipoError = null;
  let anticipoDownloadError = null;
  let anticipoDownloadMode = null;
  let anticipoContextKey = '';
  let loadedInvoiceContext = '';
  let loadRequestId = 0;
  let reopenLoading = false;
  let reopenError = null;
  let reopenSuccess = '';

  // ── Correzione di una fattura scartata ───────────────────────
  // Il modulo di correzione è l'unica strada, dal browser, verso
  // PATCH /invoices/:id. Si apre da solo quando la rimessa in coda viene
  // rifiutata: quel rifiuto elenca esattamente i campi da sistemare, e
  // chiuderlo in un banner vorrebbe dire chiedere all'utente di ricordarseli.
  let correctionOpen = false;

  // Download dei file della fattura (XML e busta firmata .p7m)
  let downloadingXml = false;
  let downloadingP7m = false;
  // Layout della copia di cortesia in scaricamento ('branded' | 'ade' | null).
  let downloadingPdf = null;
  // Dopo un download: l'avviso da mostrare quando quella copia non contiene
  // l'XML (chiave i18n), o null.
  let pdfXmlNotice = null;
  let downloadError = null;

  // Tranche UI state
  let trancheError = null;
  let showTrancheForm = false;
  let editingTrancheId = null;
  let trancheForm = emptyTrancheForm();
  let importingTranches = false;

  function emptyTrancheForm() {
    return {
      numeroTranche: '',
      dataScadenza: '',
      importo: '',
      modalitaPagamento: '',
      iban: '',
      note: '',
      dataPagamento: '',
      importoPagato: '',
      metodoEffettivo: '',
    };
  }

  $: invoiceId = params.id;
  $: companyId = $activeCompanyId;

  // Keep the lazy-loaded quote tied to both parts of the route. The router
  // reuses this component across invoice links, and the company switcher can
  // change the tenant without unmounting the page.
  $: currentInvoiceContext = `${companyId || ''}:${invoiceId || ''}`;
  $: if (currentInvoiceContext !== anticipoContextKey) {
    anticipoContextKey = currentInvoiceContext;
    anticipoData = null;
    anticipoLoading = false;
    anticipoError = null;
    anticipoDownloadError = null;
    anticipoDownloadMode = null;
    loadedInvoiceContext = '';
  }
  // Anticipo fatture is a dedicated feature, switched on per account: without
  // it neither the section nor its request exists (the server refuses with
  // 403 feature_not_enabled anyway). Four answers, not two: still waiting (no
  // section yet, so nothing flashes), could not be read (a small retry, so a
  // holder is not silently left without it), no (hidden), yes (the section).
  $: anticipoEligible = loadedInvoiceContext === currentInvoiceContext
    && isAnticipoFatturaEligible($activeCompany, invoice);
  $: anticipoAccess = featureAccess($capabilities, $capabilitiesStatus, 'anticipo-fattura');
  $: anticipoViewNow = anticipoView(anticipoEligible, anticipoAccess);
  $: anticipoAvailable = anticipoViewNow === 'section';

  // Loading is tied to the id and the company, not to the mount:
  //  - svelte-spa-router reuses this component between two /invoices/:id
  //    routes, updating `params` in place without remounting, so a
  //    detail→detail link would otherwise keep showing the previous invoice;
  //  - a deep link opened in a fresh browser mounts this page before
  //    loadCompanies() has resolved, and requesting /api/companies//invoices/x
  //    matches no route at all — a 404 with an HTML body the banner cannot read.
  $: if (invoiceId && companyId) loadData();
  $: if (!companyId) loading = $companiesLoading;

  /**
   * @param {{silent?: boolean}} [opts] `silent` reloads the invoice without
   *   raising the page spinner. The spinner replaces the whole page, which
   *   would destroy the correction form — with its confirmation, its warnings
   *   and anything typed into it — exactly when a correction has just been
   *   saved and the operator is reading what is still missing.
   */
  async function loadData({ silent = false } = {}) {
    const requestId = ++loadRequestId;
    const cid = companyId;
    const id = invoiceId;
    if (!silent) loading = true;
    error = null;
    try {
      const [inv, hist, tx, notif, trans, trs, sig] = await Promise.all([
        companyInvoicesApi.get(cid, id),
        companyInvoicesApi.getHistory(cid, id).catch(() => []),
        companyInvoicesApi.getTransmissions(cid, id).catch(() => []),
        companyInvoicesApi.getNotifications(cid, id).catch(() => []),
        companyInvoicesApi.getTransitions(cid, id).catch(() => null),
        companyTranchesApi.list(cid, id).catch(() => []),
        companyInvoicesApi.getSignatureStatus(cid, id).catch(() => null),
      ]);
      if (requestId !== loadRequestId || cid !== companyId || id !== invoiceId) return;
      invoice = inv;
      loadedInvoiceContext = `${cid}:${id}`;
      if (inv?.direction === 'inbound') refreshUnread(cid); // opening it may just have marked it read
      history = Array.isArray(hist) ? hist : [];
      transmissions = Array.isArray(tx) ? tx : [];
      notifications = Array.isArray(notif) ? notif : [];
      transitions = trans;
      tranches = Array.isArray(trs) ? trs : [];
      signature = sig;
    } catch (err) {
      if (requestId === loadRequestId && cid === companyId && id === invoiceId) error = err;
    } finally {
      if (requestId === loadRequestId && cid === companyId && id === invoiceId) loading = false;
    }
  }

  async function loadAnticipoFattura() {
    const cid = companyId;
    const id = invoiceId;
    const context = `${cid || ''}:${id || ''}`;
    if (!cid || !id || !anticipoAvailable || anticipoLoading || anticipoData) return;

    anticipoLoading = true;
    anticipoError = null;
    anticipoDownloadError = null;
    try {
      const data = await companyInvoicesApi.getAnticipoFattura(cid, id);
      if (context !== currentInvoiceContext || context !== anticipoContextKey) return;
      anticipoData = data;
    } catch (err) {
      if (context === currentInvoiceContext && context === anticipoContextKey) anticipoError = err;
    } finally {
      if (context === currentInvoiceContext && context === anticipoContextKey) anticipoLoading = false;
    }
  }

  function anticipoAmount(mode) {
    const option = anticipoData?.options?.find((entry) => entry.mode === mode);
    return option ? formatEuro(option.amount) : '-';
  }

  async function downloadAnticipoFattura(mode) {
    const cid = companyId;
    const id = invoiceId;
    const context = `${cid || ''}:${id || ''}`;
    if (!anticipoData || anticipoDownloadMode || context !== anticipoContextKey) return;

    anticipoDownloadMode = mode;
    anticipoDownloadError = null;
    try {
      const { blob, filename } = await companyInvoicesApi.downloadAnticipoFatturaPdf(cid, id, mode);
      if (context !== currentInvoiceContext || context !== anticipoContextKey) return;
      saveBlob(blob, filename);
    } catch (err) {
      if (context === currentInvoiceContext && context === anticipoContextKey) anticipoDownloadError = err;
    } finally {
      if (context === currentInvoiceContext && context === anticipoContextKey) anticipoDownloadMode = null;
    }
  }

  let markingUnread = false;
  let readStateError = null;
  let destroyed = false;
  onDestroy(() => { destroyed = true; });

  /** «Segna come da leggere»: the rules — which body, when to go back — live in markUnreadAndReturn(). */
  async function markUnread() {
    const started = { invoiceId: invoice.id, companyId: $activeCompanyId };
    const current = () => ({ invoiceId, companyId: $activeCompanyId, destroyed });
    markingUnread = true;
    readStateError = null;
    const { error } = await markUnreadAndReturn(started, {
      setReadState: companyInvoicesApi.setReadState,
      refresh: refreshUnread,
      navigate: push,
      current,
    });
    // Shown only on the invoice it belongs to: the router may have reused this
    // component for another one while the request was out.
    if (error && stillShowing(started, current())) readStateError = error;
    markingUnread = false;
  }

  async function handleRetrySend() {
    retryLoading = true;
    error = null;
    try {
      await companyInvoicesApi.retrySend($activeCompanyId, invoiceId);
      await loadData();
    } catch (err) {
      error = err;
    } finally {
      retryLoading = false;
    }
  }

  // ── Download dei file archiviati ────────────────────────────
  // L'XML è il documento fiscale vero e proprio, in entrambe le direzioni: per
  // una fattura ricevuta è l'unica copia con valore legale, e finché il
  // download non era esposto restava leggibile solo a schermo.

  function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function handleDownloadXml() {
    downloadingXml = true;
    downloadError = null;
    try {
      const { blob, filename } = await companyInvoicesApi.downloadXml($activeCompanyId, invoiceId);
      saveBlob(blob, filename);
    } catch (err) {
      downloadError = err;
    } finally {
      downloadingXml = false;
    }
  }

  async function handleDownloadSignedP7m() {
    downloadingP7m = true;
    downloadError = null;
    try {
      const { blob, filename } = await companyInvoicesApi.downloadSignedP7m($activeCompanyId, invoiceId);
      saveBlob(blob, filename);
    } catch (err) {
      downloadError = err;
    } finally {
      downloadingP7m = false;
    }
  }

  // La copia di cortesia non ha valore legale: è la lettura in chiaro da girare
  // al cliente. Si scarica dall'accettazione della trasmissione; fuori da quegli
  // stati i pulsanti restano spenti con la spiegazione, invece di rispondere 409.
  $: canDownloadPdf = canDownloadCourtesyPdf(invoice);
  $: pdfStatusCopy = courtesyPdfStatusCopy(invoice);

  async function handleDownloadPdf(layout) {
    downloadingPdf = layout;
    downloadError = null;
    pdfXmlNotice = null;
    try {
      const { blob, filename, xml } = await companyInvoicesApi.downloadPdf($activeCompanyId, invoiceId, layout);
      saveBlob(blob, filename);
      pdfXmlNotice = courtesyXmlNoticeKey(xml);
    } catch (err) {
      downloadError = err;
    } finally {
      downloadingPdf = null;
    }
  }

  // ── Scarto → riapertura ─────────────────────────────────────
  // Una fattura scartata non è stata emessa: va corretta e ritrasmessa. La
  // riapertura la riporta in lavorazione rigenerando l'XML.
  $: isScarto = invoice?.status === 'scarto';

  // Ogni azione che modifica la fattura (reinvio, correzione, riapertura,
  // esito, firma, pagamenti, rate, «da leggere») è del ruolo di scrittura
  // nell'azienda attiva — la stessa regola del server (stores/capabilities.js).
  $: canWrite = $canWriteInCompany;

  async function handleReopen() {
    reopenLoading = true;
    reopenError = null;
    reopenSuccess = '';
    try {
      const res = await companyInvoicesApi.reopen($activeCompanyId, invoiceId);
      reopenSuccess = res.manualSignatureRequired
        ? tImmediate('invoices.reopenSuccessSignature')
        : tImmediate('invoices.reopenSuccess');
      correctionOpen = false;
      await loadData();
    } catch (err) {
      reopenError = err;
      // Il documento è stato rifiutato dalla validazione: la lista di ciò che
      // non va è l'unica cosa che serve adesso, e va accanto ai campi.
      if (Array.isArray(err?.details) && err.details.length > 0) correctionOpen = true;
    } finally {
      reopenLoading = false;
    }
  }

  // Le segnalazioni dell'ultimo tentativo di rimessa in coda, così come le ha
  // scritte il server: il modulo di correzione le smista campo per campo.
  $: reopenIssues = Array.isArray(reopenError?.details) ? reopenError.details : [];

  // Una fattura si corregge solo se è emessa da noi ed è stata scartata: è
  // l'unico rifiuto che lascia il documento correggibile, perché SdI non l'ha
  // mai accettato e il numero non è stato consumato.
  $: canCorrect = invoice?.direction === 'outbound' && invoice?.status === 'scarto';

  // La notifica di scarto più recente, cioè il messaggio con cui SdI ha detto
  // di no a questa fattura.
  $: scartoNotification = notifications.find((n) => notificationType(n) === 'notifica_scarto') || null;

  async function handleCorrected() {
    // I dati corretti (e il totale ricalcolato dal server) devono comparire
    // subito nella scheda; il modulo resta aperto con la sua conferma.
    await loadData({ silent: true });
  }

  function statusBadgeClass(status) {
    const completed = ['delivered', 'accepted', 'completed', 'decorrenza_termini', 'success'];
    const failed = ['rejected', 'send_failed', 'esito_scarto', 'notification_error', 'transport_error', 'soap_fault'];
    const pending = ['queued_for_send', 'sent', 'pending'];
    if (completed.includes(status)) return 'badge-green';
    if (failed.includes(status)) return 'badge-red';
    if (pending.includes(status)) return 'badge-yellow';
    return 'badge-cyan';
  }

  function formatDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }

  // L'esito committente esiste solo per le fatture FPA12 indirizzate a una PA:
  // su una FPR12 privata il backend rifiuta l'invio con un 400.
  function canSendEsito() {
    return invoice?.direction === 'inbound'
      && invoice?.formatoTrasmissione === 'FPA12'
      && transitions?.allowedTransitions?.includes('esito_sent');
  }

  // `error` means the automatic retries were exhausted, not that the document
  // is void: SdI never took the file in, so the number was not consumed and the
  // same invoice can be sent again once the cause is fixed.
  // An attempt whose request may have reached SdI and was never answered:
  // the server refuses every re-send until it is closed on evidence, so the
  // page explains it instead of offering a button that would be refused.
  $: unknownAttempts = transmissions.filter((tx) => tx.operation === 'RiceviFile'
    && tx.status === 'unknown' && !tx.resolvedAt);
  $: outcomeUnknown = invoice?.direction === 'outbound' && unknownAttempts.length > 0;

  function canRetrySend() {
    return ['rejected_by_transport', 'error'].includes(invoice?.status)
      && invoice?.direction === 'outbound'
      && !outcomeUnknown;
  }

  $: isSendStopped = invoice?.status === 'error' && invoice?.direction === 'outbound' && !outcomeUnknown;

  function handleEsitoSent() {
    showEsitoModal = false;
    loadData();
  }

  // ── Tranches ────────────────────────────────────────────────
  // The payment summary is derived from these same instalments, so it is
  // re-read with them: an edit here must not leave the card above showing the
  // residual from before it.
  async function reloadTranches() {
    try {
      const [trs, inv] = await Promise.all([
        companyTranchesApi.list($activeCompanyId, invoiceId),
        companyInvoicesApi.get($activeCompanyId, invoiceId),
      ]);
      tranches = trs;
      invoice = inv;
    } catch (err) {
      trancheError = err;
    }
  }

  function openAddTrancheForm() {
    trancheForm = emptyTrancheForm();
    trancheForm.numeroTranche = String((tranches?.length || 0) + 1);
    editingTrancheId = null;
    showTrancheForm = true;
    trancheError = null;
  }

  function openEditTrancheForm(tr) {
    editingTrancheId = tr.id;
    trancheForm = {
      numeroTranche: tr.numeroTranche != null ? String(tr.numeroTranche) : '',
      dataScadenza: tr.dataScadenza ? String(tr.dataScadenza).slice(0, 10) : '',
      importo: tr.importo != null ? String(tr.importo) : '',
      modalitaPagamento: tr.modalitaPagamento || '',
      iban: tr.iban || '',
      note: tr.note || '',
      dataPagamento: tr.dataPagamento ? String(tr.dataPagamento).slice(0, 10) : '',
      importoPagato: tr.importoPagato != null ? String(tr.importoPagato) : '',
      metodoEffettivo: tr.metodoEffettivo || '',
    };
    showTrancheForm = true;
    trancheError = null;
  }

  function cancelTrancheForm() {
    showTrancheForm = false;
    editingTrancheId = null;
    trancheError = null;
  }

  function payloadFromForm() {
    const payload = {};
    if (trancheForm.numeroTranche) payload.numeroTranche = parseInt(trancheForm.numeroTranche, 10);
    payload.dataScadenza = trancheForm.dataScadenza || null;
    payload.importo = trancheForm.importo ? Number(trancheForm.importo) : null;
    payload.modalitaPagamento = trancheForm.modalitaPagamento || null;
    payload.iban = trancheForm.iban || null;
    payload.note = trancheForm.note || null;
    payload.dataPagamento = trancheForm.dataPagamento || null;
    payload.importoPagato = trancheForm.importoPagato ? Number(trancheForm.importoPagato) : null;
    payload.metodoEffettivo = trancheForm.metodoEffettivo || null;
    return payload;
  }

  async function submitTrancheForm() {
    trancheError = null;
    const payload = payloadFromForm();
    if (!editingTrancheId && (payload.importo == null || Number.isNaN(payload.importo))) {
      // A whole Italian sentence, marked as ours, so ErrorBanner shows it as
      // written. The old value was the bare field label 'Importo *', which
      // friendlyError() could not classify and replaced with the generic
      // "Si è verificato un problema" — the one message that does not say what
      // the user forgot.
      trancheError = localizedError(tImmediate('invoices.tranches.errors.importoRequired'));
      return;
    }
    try {
      const cid = $activeCompanyId;
      if (editingTrancheId) {
        await companyTranchesApi.update(cid, invoiceId, editingTrancheId, payload);
      } else {
        await companyTranchesApi.create(cid, invoiceId, payload);
      }
      await reloadTranches();
      cancelTrancheForm();
    } catch (err) {
      trancheError = err;
    }
  }

  async function markTranchePaid(tr) {
    const today = todayCalendarDay();
    try {
      await companyTranchesApi.update($activeCompanyId, invoiceId, tr.id, {
        dataPagamento: today,
        importoPagato: tr.importoPagato != null ? tr.importoPagato : tr.importo,
      });
      await reloadTranches();
    } catch (err) {
      trancheError = err;
    }
  }

  async function markTrancheUnpaid(tr) {
    try {
      await companyTranchesApi.update($activeCompanyId, invoiceId, tr.id, {
        dataPagamento: null,
        importoPagato: null,
        metodoEffettivo: null,
      });
      await reloadTranches();
    } catch (err) {
      trancheError = err;
    }
  }

  async function deleteTranche(tr) {
    if (!confirm(tImmediate('invoices.tranches.confirmDelete'))) return;
    try {
      await companyTranchesApi.remove($activeCompanyId, invoiceId, tr.id);
      await reloadTranches();
    } catch (err) {
      trancheError = err;
    }
  }

  async function importTranchesFromXml() {
    importingTranches = true;
    trancheError = null;
    try {
      await companyTranchesApi.importFromXml($activeCompanyId, invoiceId);
      await reloadTranches();
    } catch (err) {
      trancheError = err;
    } finally {
      importingTranches = false;
    }
  }

  function formatEuro(n) {
    if (n == null || Number.isNaN(Number(n))) return '-';
    return Number(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function formatShortDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  function trancheStatusInfo(tr) {
    if (instalmentShownPaid(tr)) return { label: tImmediate('invoices.tranches.statusPaid'), className: 'badge-green' };
    if (tr.dataScadenza) {
      const now = new Date();
      const due = new Date(tr.dataScadenza);
      const days = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
      if (days < 0) return { label: tImmediate('invoices.tranches.statusOverdue'), className: 'badge-red' };
      if (days <= 7) return { label: tImmediate('invoices.tranches.statusDueSoon'), className: 'badge-yellow' };
    }
    return { label: tImmediate('invoices.tranches.statusScheduled'), className: 'badge-cyan' };
  }

  function trancheSourceLabel(src) {
    if (src === 'xml') return tImmediate('invoices.tranches.sourceXml');
    if (src === 'created') return tImmediate('invoices.tranches.sourceCreated');
    if (src === 'payment') return tImmediate('invoices.tranches.sourcePayment');
    return tImmediate('invoices.tranches.sourceManual');
  }

  $: tranchesPlannedTotal = tranches.reduce((sum, t) => sum + Number(t.importo || 0), 0);
  $: tranchesPaidTotal = tranches.reduce((sum, t) => sum + Number((t.dataPagamento ? (t.importoPagato ?? t.importo) : 0) || 0), 0);
  $: tranchesOutstanding = tranchesPlannedTotal - tranchesPaidTotal;
  $: invoiceTotalNum = invoice?.totalAmount != null ? Number(invoice.totalAmount) : null;
  $: trancheTotalsMismatch = invoiceTotalNum != null
    && tranches.length > 0
    && Math.abs(tranchesPlannedTotal - invoiceTotalNum) > 0.01;

  /**
   * Correlated documents, read from metadata.datiGenerali.
   *
   * In XSD sequence order, so a document carrying both an order and a contract
   * reads the way the XML is written. Every block is stored as an array (a
   * contract reference may repeat), but a single object is tolerated here so a
   * hand-written or older metadata payload still renders.
   */
  const CORRELATI_BLOCK_KEYS = ['ordineAcquisto', 'contratto', 'convenzione', 'ricezione', 'fattureCollegate'];

  // NumItem, CodiceCommessaConvenzione and RiferimentoNumeroLinea have no field
  // on the create form, but an imported invoice can carry them and this view is
  // the only place they ever become visible.
  const CORRELATO_FIELD_KEYS = [
    'idDocumento', 'data', 'numItem', 'codiceCommessaConvenzione',
    'codiceCUP', 'codiceCIG', 'riferimentoNumeroLinea',
  ];

  function correlatoFields(entry) {
    const out = [];
    for (const key of CORRELATO_FIELD_KEYS) {
      const value = entry?.[key];
      if (value == null || value === '') continue;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        out.push({ key, text: value.join(', ') });
        continue;
      }
      out.push({ key, text: key === 'data' ? formatShortDate(value) : String(value) });
    }
    return out;
  }

  $: datiGenerali = invoice?.metadata?.datiGenerali || {};

  $: correlatiBlocks = CORRELATI_BLOCK_KEYS
    .map((key) => {
      const raw = datiGenerali[key];
      const entries = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      return { key, entries: entries.filter((e) => e && typeof e === 'object') };
    })
    .filter((block) => block.entries.length > 0);

  // The importer reads FatturaPrincipale straight out of the XML, where the
  // children are NumeroFatturaPrincipale/DataFatturaPrincipale; accept both
  // spellings rather than showing an empty card for an imported autofattura.
  $: fatturaPrincipale = (() => {
    const fp = datiGenerali.fatturaPrincipale;
    if (!fp || typeof fp !== 'object') return null;
    const numero = fp.numero ?? fp.numeroFatturaPrincipale ?? '';
    const data = fp.data ?? fp.dataFatturaPrincipale ?? '';
    return numero || data ? { numero, data } : null;
  })();

  $: supplierInvoiceReference = invoice?.supplierInvoiceReference ?? fatturaPrincipale;
  $: isSupplierReferenceAutofattura = invoice?.metadata?.autofattura?.flow === 'integrazione_acquisti_estero'
    && ['TD17', 'TD18'].includes(
      invoice?.metadata?.autofattura?.tipoDocumento || datiGenerali.tipoDocumento,
    );
  $: supplierInvoiceReferenceTitle = supplierInvoiceReference?.element === 'fattureCollegate'
    ? 'invoices.correlati.types.fatturaCollegataFornitore'
    : supplierInvoiceReference?.element === 'fatturaPrincipale'
      ? 'invoices.correlati.types.fatturaPrincipale'
      : isSupplierReferenceAutofattura
        ? 'invoices.correlati.types.fatturaFornitore'
        : 'invoices.correlati.types.fatturaPrincipale';

  $: hasCorrelati = correlatiBlocks.length > 0 || supplierInvoiceReference != null;
</script>

<Layout>
  {#if loading}
    <PageLoader />
  {:else if error}
    <ErrorBanner {error} />
    <button class="btn btn-secondary btn-sm" on:click={() => push('/invoices')}>{$t('invoices.backToList')}</button>
  {:else if invoice}
    <!-- Header: what this document is, who it is with, where it stands. -->
    <a href="/invoices" use:link class="btn-link mb-4 -ml-1">
      <ArrowLeft size={14} aria-hidden="true" /> {$t('invoices.backToList')}
    </a>
    <div class="view-head">
      <div class="detail-head min-w-0">
        <h1 class="m-0">
          <span class="eyebrow block mb-1">{invoice.direction === 'inbound' ? $t('invoices.detail.received') : $t('invoices.detail.issued')}</span>
          <span class="detail-number">{invoice.invoiceNumber || invoice.progressivoInvio || $t('nav.invoices')}</span>
        </h1>
        <div class="flex flex-wrap items-center gap-2">
          <StatusBadge status={invoice.status} {invoice} size="lg" help />
          {#if pagamentoBadge && pagamento?.stato !== 'nonApplicabile'}
            <span class="badge {pagamentoBadge.cls}" title={$t(`invoices.pagamento.statoHelp.${pagamento.stato}`)}>{$t(pagamentoBadge.key)}</span>
          {/if}
          <DirectionBadge direction={invoice.direction} />
        </div>
        {#if counterparty}
          <p class="detail-title">{counterparty}</p>
        {/if}
        <p class="text-muted text-sm inline-flex flex-wrap items-center gap-1">
          {$t('invoices.fields.identificativoSdi')}: <span class="mono">{invoice.identificativoSdi || invoice.id.slice(0, 8) + '…'}</span><InfoHint term="identificativoSdi" />
        </p>
      </div>
      <div class="view-actions">
        {#if canMarkUnread(invoice) && canWrite}
          <button type="button" class="btn btn-secondary btn-sm gap-1.5"
            title={$t('invoices.readState.markUnreadTooltip')} disabled={markingUnread} on:click={markUnread}>
            <MailOpen size={14} aria-hidden="true" /> {$t('invoices.readState.markUnread')}
          </button>
        {/if}
        {#if canWrite && canRetrySend()}
          <button class="btn btn-accent btn-sm" on:click={handleRetrySend} disabled={retryLoading}>
            <RefreshCw size={14} class="mr-1 {retryLoading ? 'animate-spin' : ''}" aria-hidden="true" />
            {retryLoading ? $t('app.resending') : $t('invoices.retrySend')}
          </button>
        {/if}
        {#if canWrite && canSendEsito()}
          <button class="btn btn-accent btn-sm" on:click={() => showEsitoModal = true}>
            <Send size={14} class="mr-1" aria-hidden="true" /> {$t('invoices.sendEsito')}
          </button>
        {/if}
        {#if signature?.manual && !canWrite && signature.attached}
          <span class="inline-flex items-center gap-1 text-sm text-muted">
            <ShieldCheck size={14} aria-hidden="true" /> {$t('invoices.signatureAttached')}
          </span>
        {:else if signature?.manual && canWrite}
          <button
            class="btn {signature.attached ? 'btn-secondary' : 'btn-primary'} btn-sm"
            on:click={() => showSignModal = true}
          >
            {#if signature.attached}
              <ShieldCheck size={14} class="mr-1" aria-hidden="true" /> {$t('invoices.signatureAttached')}
            {:else}
              <FileSignature size={14} class="mr-1" aria-hidden="true" /> {$t('invoices.signAndSend')}
            {/if}
          </button>
        {/if}
      </div>
    </div>

    <div class="detail-amount">
      <span>{$t('invoices.fields.totalAmount')}</span>
      <strong>{formatEuro(invoice.totalAmount)}</strong>
    </div>
    <ErrorBanner error={readStateError} />

    <!-- Uno scarto è la bocciatura della fattura da parte di SdI: spiega cosa
         significa, la finestra dei 5 giorni, e offri la riapertura. -->
    <!-- L'esito della riapertura vive fuori dal banner: appena la fattura torna
         in lavorazione non è più scartata, e il messaggio deve restare leggibile. -->
    {#if reopenError}
      <ErrorBanner error={reopenError} title={$t('invoices.reopenErrorTitle')} />
    {/if}
    {#if reopenSuccess}
      <div role="status" class="alert alert-success mb-6">
        <p class="alert-message">{reopenSuccess}</p>
      </div>
    {/if}

    {#if isScarto}
      <div class="next-step next-step-danger">
        <h2 class="flex items-center gap-2">
          <XCircle size={16} class="text-danger" aria-hidden="true" /> {$t('invoices.scartoBannerTitle')}
        </h2>
        <p class="text-sm mt-2" style="color: var(--color-text-body);">
          {$t('invoices.scartoBannerBody')}<InfoHint term="scarto" />
        </p>
        <!-- Someone who cannot act is told who can, not pointed at buttons and
             tabs their role does not show. -->
        <p class="text-sm mt-2" style="color: var(--color-text-body);">
          {canWrite ? $t('invoices.scartoBannerDeadline') : $t('invoices.scartoBannerDeadlineReader')}
        </p>
        <p class="text-xs mt-2 text-muted">
          {canWrite ? $t('invoices.scartoBannerHowTo') : $t('invoices.scartoBannerHowToReader')}
        </p>

        {#if canWrite}
        <div class="flex flex-wrap gap-2 mt-3">
          {#if canCorrect}
            <button class="btn btn-primary btn-sm" on:click={() => correctionOpen = !correctionOpen}>
              <FileEdit size={14} class="mr-1" />
              {correctionOpen ? $t('invoices.correction.close') : $t('invoices.correction.open')}
            </button>
          {/if}
          <button class="btn btn-secondary btn-sm" on:click={handleReopen} disabled={reopenLoading}>
            <RefreshCw size={14} class="mr-1 {reopenLoading ? 'animate-spin' : ''}" />
            {reopenLoading ? $t('invoices.reopenPending') : $t('invoices.reopen')}
          </button>
        </div>
        {/if}
      </div>

      {#if canWrite && correctionOpen}
        <InvoiceCorrectionForm
          companyId={$activeCompanyId}
          {invoiceId}
          {invoice}
          {reopenIssues}
          {scartoNotification}
          on:saved={handleCorrected}
          on:close={() => correctionOpen = false}
        />
      {/if}
    {/if}

    <!-- Esito sconosciuto: la richiesta può essere arrivata allo SdI. Solo
         spiegazione, nessun pulsante: il reinvio è bloccato lato server. -->
    {#if outcomeUnknown}
      <div class="next-step next-step-danger" role="status">
        <h2 class="flex items-center gap-2">
          <AlertTriangle size={16} class="text-danger" aria-hidden="true" /> {$t('invoices.outcomeUnknown.title')}<InfoHint term="esitoSconosciuto" />
        </h2>
        <p class="text-sm mt-2" style="color: var(--color-text-body);">{$t('invoices.outcomeUnknown.body')}</p>
        <ul class="text-xs mt-2 text-muted">
          {#each unknownAttempts as attempt}
            <li>{$t('invoices.outcomeUnknown.fileLabel')}: <span class="mono">{attempt.nomeFile || '—'}</span> · {formatDate(attempt.requestStartedAt || attempt.createdAt)}</li>
          {/each}
        </ul>
        <p class="text-xs mt-2 text-muted">{$t('invoices.outcomeUnknown.whatToDo')}</p>
        <p class="text-xs mt-2 text-muted">{$t('invoices.outcomeUnknown.readOnly')}</p>
      </div>
    {/if}

    <!-- «Invio interrotto» suona come una fattura persa. Non lo è: SdI non l'ha
         mai presa in carico, quindi il numero non è consumato e basta rimetterla
         in coda. Senza questo riquadro l'utente non ha modo di saperlo. -->
    {#if isSendStopped}
      <div class="next-step next-step-danger">
        <h2 class="flex items-center gap-2">
          <XCircle size={16} class="text-danger" aria-hidden="true" /> {$t('invoices.sendStoppedBannerTitle')}
        </h2>
        <p class="text-sm mt-2" style="color: var(--color-text-body);">
          {$t('invoices.sendStoppedBannerBody')}
        </p>
        <p class="text-xs mt-2 text-muted">
          {canWrite ? $t('invoices.sendStoppedBannerHowTo') : $t('invoices.sendStoppedBannerHowToReader')}
        </p>

        {#if canWrite}
          <button class="btn btn-primary btn-sm mt-3" on:click={handleRetrySend} disabled={retryLoading}>
            <RefreshCw size={14} class="mr-1 {retryLoading ? 'animate-spin' : ''}" />
            {retryLoading ? $t('app.resending') : $t('invoices.retrySend')}
          </button>
        {/if}
      </div>
    {/if}

    <!-- A PA invoice waiting for its signature never leaves the queue, so say
         plainly why nothing is happening and what to do about it. -->
    {#if signature?.manual && !signature.attached}
      <div class="next-step next-step-warning">
        <h2 class="flex items-center gap-2">
          <FileSignature size={16} class="text-warning" aria-hidden="true" /> {$t('invoices.signatureBannerTitle')}
        </h2>
        <p class="text-sm mt-2" style="color: var(--color-text-body);">
          {canWrite ? $t('invoices.signatureBannerBody') : $t('invoices.signatureBannerBodyReader')}<InfoHint term="firmaDigitale" />
        </p>
        {#if canWrite}
          <button class="btn btn-primary btn-sm mt-3" on:click={() => showSignModal = true}>
            <FileSignature size={14} class="mr-1" /> {$t('invoices.signAndSend')}
          </button>
        {/if}
      </div>
    {/if}

    <!-- Lo stato del pagamento, calcolato dalle rate a ogni lettura. Una fattura
         che non è un debito tra le parti (bozza, scartata, autofattura) non ne ha uno. -->
    {#if pagamento && pagamento.stato !== 'nonApplicabile'}
      <section class="mb-8" aria-labelledby="payment-title">
        <div class="section-heading">
          <h2 id="payment-title" class="inline-flex items-center gap-2">
            {$t('invoices.pagamento.summary.title')}<InfoHint term="statoPagamento" />
          </h2>
          {#if canWrite && isPaymentOpen(pagamento)}
            <button type="button" class="btn btn-primary btn-sm gap-1.5"
              title={$t('invoices.pagamento.recordTooltip')} on:click={() => (payingOpen = true)}>
              <Wallet size={14} aria-hidden="true" /> <span>{$t('invoices.pagamento.record')}</span>
            </button>
          {/if}
        </div>
        <p class="text-sm text-muted mb-4 measure">{$t(`invoices.pagamento.statoHelp.${pagamento.stato}`)}</p>
        <dl class="detail-meta">
          <div>
            <dt>{$t('invoices.pagamento.summary.importo')}</dt>
            <dd class="mono">{formatEuro(pagamento.importo)}</dd>
          </div>
          <div>
            <dt>{$t('invoices.pagamento.summary.pagato')}</dt>
            <dd class="mono text-success">{formatEuro(pagamento.pagato)}</dd>
          </div>
          <div>
            <dt>{$t('invoices.pagamento.summary.residuo')}</dt>
            <dd class="mono" class:text-accent={pagamento.residuo > 0}>{formatEuro(pagamento.residuo)}</dd>
          </div>
          <div>
            <dt>{$t('invoices.pagamento.summary.prossimaScadenza')}</dt>
            <dd>{pagamento.prossimaScadenza ? formatShortDate(pagamento.prossimaScadenza) : $t('invoices.pagamento.summary.nessuna')}</dd>
          </div>
        </dl>
      </section>
    {/if}

    <!-- The invoice's own data, then everything else behind disclosures: the
         page opens on what matters and keeps every detail one click away. -->
    <div class="grid gap-8 lg:grid-cols-2 mb-8">
      <section aria-labelledby="invoice-data-title">
        <div class="section-heading"><h2 id="invoice-data-title">{$t('invoices.sections.invoiceInfo')}</h2></div>
        <!-- `environment` and `formatoTrasmissione` go through the shared label
             helpers: the raw enums ("test", "FPR12") tell someone issuing their
             first invoice nothing. -->
        <dl class="detail-meta">
          {#each [
            [$t('invoices.fields.invoiceNumber'), invoice.invoiceNumber, ''],
            [$t('invoices.fields.invoiceDate'), invoice.invoiceDate ? new Date(invoice.invoiceDate).toLocaleDateString('it-IT') : '-', ''],
            [$t('invoices.fields.totalAmount'), formatEuro(invoice.totalAmount), ''],
            [$t('invoices.fields.direction'), directionLabel(invoice.direction), ''],
            [$t('invoices.fields.status'), statusLabel(invoice.status), ''],
            [$t('invoices.fields.format'), formatoLabel(invoice.formatoTrasmissione), 'fatturaPA'],
            [$t('invoices.fields.environment'), environmentLabel(invoice.environment), 'ambiente'],
            [$t('invoices.fields.id'), invoice.id, ''],
          ] as [label, value, term]}
            <div>
              <dt class="inline-flex items-center gap-1">{label}{#if term}<InfoHint {term} />{/if}</dt>
              <dd>{value || '-'}</dd>
            </div>
          {/each}
          {#if invoice.direction === 'inbound'}
            <div>
              <dt class="inline-flex items-center gap-1">{$t('invoices.receiptDate.label')}<InfoHint term="dataRicezioneSdi" /></dt>
              <dd>{invoice.dataRicezioneSdi
                ? new Date(invoice.dataRicezioneSdi).toLocaleDateString('it-IT', {
                  timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric',
                })
                : invoice.metadata?.imported ? $t('invoices.receiptDate.importedUnavailable') : '-'}</dd>
            </div>
          {/if}
        </dl>
      </section>

      <section aria-labelledby="sdi-data-title">
        <div class="section-heading"><h2 id="sdi-data-title">{$t('invoices.sections.sdiInfo')}</h2></div>
        <dl class="detail-meta">
          {#each [
            [$t('invoices.fields.identificativoSdi'), invoice.identificativoSdi],
            [$t('invoices.fields.senderTaxId'), invoice.senderTaxId],
            [$t('invoices.fields.receiverTaxId'), invoice.receiverTaxId],
            [$t('invoices.fields.progressivoInvio'), invoice.progressivoInvio],
            [$t('invoices.fields.codiceDestinatario'), invoice.codiceDestinatario],
            [$t('invoices.fields.pecDestinatario'), invoice.pecDestinatario],
          ] as [label, value]}
            <div>
              <dt>{label}</dt>
              <dd class="mono">{value || '-'}</dd>
            </div>
          {/each}
          <!-- The idempotency key is an internal anti-duplicate token: it means
               nothing to the person reading the invoice and everything to
               whoever is debugging a double send. Admin-only, like the
               transmissions and the allowed transitions. -->
          {#if $isAdmin}
            <div>
              <dt class="inline-flex items-center gap-1">
                {$t('invoices.fields.idempotencyKey')}<InfoHint text={$t('invoices.fields.idempotencyKeyHint')} />
              </dt>
              <dd class="mono">{invoice.idempotencyKey || '-'}</dd>
            </div>
          {/if}
        </dl>
      </section>
    </div>

    <!-- La firma del fornitore su una fattura ricevuta (#44): l'esito del
         controllo in background, solo informativo. Nulla da mostrare per le
         fatture emesse e per quelle ricevute senza firma. -->
    {#if invoice.direction === 'inbound'}
      <SignatureVerificationCard {signature} imported={invoice.status === 'imported'} />
    {/if}

    <!-- I file archiviati. La firma (.p7m) compare solo quando ne esiste
         davvero una: su una fattura B2B non firmata un pulsante che risponde
         409 insegnerebbe soltanto che qualcosa non funziona. -->
    <details class="disclosure">
      <summary>{$t('invoices.files.title')}</summary>
      <div class="disclosure-content">
          <p class="text-xs text-muted mb-4 measure">
            {invoice.direction === 'inbound'
              ? $t('invoices.files.descriptionInbound')
              : $t('invoices.files.descriptionOutbound')}
          </p>

          {#if downloadError}
            <ErrorBanner error={downloadError} title={$t('invoices.files.errorTitle')} />
          {/if}

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <button class="btn btn-secondary btn-sm" on:click={handleDownloadXml} disabled={downloadingXml}>
                <Download size={14} class="mr-1 {downloadingXml ? 'animate-pulse' : ''}" />
                {downloadingXml ? $t('invoices.files.downloading') : $t('invoices.files.downloadXml')}
              </button>
              <p class="text-xs text-muted mt-2 measure">
                {$t('invoices.files.xmlHint')}<InfoHint term="fatturaPA" />
              </p>
            </div>

            {#if signature?.attached}
              <div>
                <button class="btn btn-secondary btn-sm" on:click={handleDownloadSignedP7m} disabled={downloadingP7m}>
                  <ShieldCheck size={14} class="mr-1 {downloadingP7m ? 'animate-pulse' : ''}" />
                  {downloadingP7m ? $t('invoices.files.downloading') : $t('invoices.files.downloadP7m')}
                </button>
                <p class="text-xs text-muted mt-2 measure">
                  <!-- Su una fattura ricevuta la busta è del fornitore, non
                       nostra: dirle entrambe con la stessa frase racconterebbe
                       a chi legge una firma che non ha apposto. -->
                  {invoice.direction === 'inbound'
                    ? $t('invoices.files.p7mHintInbound')
                    : $t('invoices.files.p7mHint')}<InfoHint term="p7m" />
                </p>
              </div>
            {/if}
          </div>

          <!-- Copia di cortesia: solo per le fatture emesse da noi. Le ricevute
               hanno già il loro XML originale e la "stampa" non è nostra. -->
          {#if invoice.direction === 'outbound'}
            <div class="mt-6 pt-5 border-t" style="border-color: var(--color-tint-medium);">
              <h4 class="eyebrow text-muted mb-1">
                {$t('invoices.files.pdfTitle')}<InfoHint text={$t('invoices.files.pdfTitleHint')} />
              </h4>
              <p class="text-xs text-muted mb-4 measure">{$t('invoices.files.pdfDescription')}</p>

              {#if pdfStatusCopy}
                <p class="text-xs mb-4 measure" style="color: var(--color-text-body);">
                  {$t(pdfStatusCopy.messageKey)}
                  {#if pdfStatusCopy.hintKey}<InfoHint text={$t(pdfStatusCopy.hintKey)} />{/if}
                </p>
              {/if}

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <button class="btn btn-secondary btn-sm" on:click={() => handleDownloadPdf('branded')}
                    disabled={!canDownloadPdf || downloadingPdf !== null}>
                    <FileText size={14} class="mr-1 {downloadingPdf === 'branded' ? 'animate-pulse' : ''}" />
                    {downloadingPdf === 'branded' ? $t('invoices.files.downloading') : $t('invoices.files.downloadPdf')}
                  </button>
                  <p class="text-xs text-muted mt-2 measure">
                    {$t('invoices.files.pdfBrandedHint')}<InfoHint text={$t('invoices.files.pdfBrandedInfo')} />
                  </p>
                </div>

                <div>
                  <button class="btn btn-secondary btn-sm" on:click={() => handleDownloadPdf('ade')}
                    disabled={!canDownloadPdf || downloadingPdf !== null}>
                    <FileText size={14} class="mr-1 {downloadingPdf === 'ade' ? 'animate-pulse' : ''}" />
                    {downloadingPdf === 'ade' ? $t('invoices.files.downloading') : $t('invoices.files.downloadPdfAde')}
                  </button>
                  <p class="text-xs text-muted mt-2 measure">
                    {$t('invoices.files.pdfAdeHint')}<InfoHint text={$t('invoices.files.pdfAdeInfo')} />
                  </p>
                </div>
              </div>

              <!-- La copia appena scaricata non contiene l'XML: detto subito,
                   perché il testo sopra lo promette "di norma". -->
              {#if pdfXmlNotice}
                <div role="status" aria-live="polite" class="alert alert-info mt-4">
                  <Info size={16} class="alert-mark mt-0.5" aria-hidden="true" />
                  <p class="alert-message min-w-0 flex-1">{$t(pdfXmlNotice)}</p>
                </div>
              {/if}
            </div>

            <CourtesyEmailPanel {invoice} companyId={$activeCompanyId} />
          {/if}
      </div>
    </details>

    <!-- Correlated documents: only when the invoice carries one. -->
    {#if hasCorrelati}
      <details class="disclosure">
        <summary class="inline-flex items-center gap-1">{$t('invoices.sections.correlati')}</summary>
        <div class="disclosure-content">
          <p class="text-xs text-muted mb-2 measure inline-flex items-center gap-1">{$t('invoices.correlati.description')}<InfoHint term="documentiCorrelati" /></p>


            {#each correlatiBlocks as block}
              {#each block.entries as entry, i}
                <div class="border-t pt-3 mt-3" style="border-color: var(--color-border-default);">
                  <p class="eyebrow mb-2" style="color: var(--color-primary);">
                    {$t(`invoices.correlati.types.${block.key}`)}{block.entries.length > 1 ? ` (${i + 1}/${block.entries.length})` : ''}
                  </p>
                  <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {#each correlatoFields(entry) as field}
                      <div class="flex justify-between gap-3">
                        <dt class="eyebrow text-muted inline-flex items-center gap-1">
                          {$t(`invoices.correlati.fields.${field.key}`)}{#if field.key === 'codiceCIG'}<InfoHint term="codiceCIG" />{:else if field.key === 'codiceCUP'}<InfoHint term="codiceCUP" />{/if}
                        </dt>
                        <dd class="text-sm text-heading text-right break-all">{field.text}</dd>
                      </div>
                    {/each}
                  </dl>
                </div>
              {/each}
            {/each}

            {#if supplierInvoiceReference}
              <div class="border-t pt-3 mt-3" style="border-color: var(--color-border-default);">
                <p class="eyebrow mb-2 inline-flex items-center gap-1" style="color: var(--color-primary);">
                  {$t(supplierInvoiceReferenceTitle)}<InfoHint term="fatturaPrincipale" />
                </p>
                <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                  <div class="flex justify-between gap-3">
                    <dt class="eyebrow text-muted">{$t('invoices.correlati.fields.numero')}</dt>
                    <dd class="text-sm text-heading text-right break-all">{supplierInvoiceReference.numero || '-'}</dd>
                  </div>
                  <div class="flex justify-between gap-3">
                    <dt class="eyebrow text-muted">{$t('invoices.correlati.fields.data')}</dt>
                    <dd class="text-sm text-heading text-right">{supplierInvoiceReference.data ? formatShortDate(supplierInvoiceReference.data) : '-'}</dd>
                  </div>
                </dl>
              </div>
            {/if}
        </div>
      </details>
    {/if}

    {#if anticipoAvailable}
      <details class="disclosure" on:toggle={onAnticipoToggle}>
        <summary>{$t('invoices.tabs.anticipo')}</summary>
        <section class="disclosure-content max-w-3xl" aria-labelledby="anticipo-title">
          <h3 id="anticipo-title" class="sr-only">{$t('invoices.anticipo.title')}</h3>
          <p class="text-sm text-muted">{$t('invoices.anticipo.description')} <FeatureBadge kind="dedicated" /></p>

          {#if anticipoLoading}
            <p class="mt-5 text-sm" role="status" aria-live="polite">{$t('invoices.anticipo.loading')}</p>
          {:else if anticipoError}
            <ErrorBanner error={anticipoError} title={$t('invoices.anticipo.loadErrorTitle')} />
            <button class="btn btn-secondary btn-sm mt-3" on:click={loadAnticipoFattura} disabled={anticipoLoading}>
              <RefreshCw size={14} class="mr-1 {anticipoLoading ? 'animate-spin' : ''}" /> {$t('invoices.anticipo.retry')}
            </button>
          {:else if anticipoData}
            <div class="mt-5 border-w p-4" style="border-color: var(--color-border-default);">
              <p class="eyebrow text-muted">{$t('invoices.anticipo.date')}</p>
              <p class="mt-1 text-lg font-bold text-heading">{anticipoData.date}</p>
              <p class="mt-3 text-sm text-muted">
                {$t('invoices.anticipo.pdfDescription', { date: anticipoData.date })}
              </p>
            </div>

            <div class="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {#each ['80', '100'] as mode}
                <button
                  class="btn btn-primary min-h-20 flex flex-col items-start justify-center gap-1 text-left"
                  on:click={() => downloadAnticipoFattura(mode)}
                  disabled={anticipoDownloadMode !== null}
                  aria-label="{$t(`invoices.anticipo.options.${mode}`)}: {anticipoAmount(mode)}"
                >
                  <span class="inline-flex items-center gap-2">
                    {#if anticipoDownloadMode === mode}
                      <RefreshCw size={14} class="animate-spin" />
                    {:else}
                      <Download size={14} />
                    {/if}
                    {$t(`invoices.anticipo.options.${mode}`)}
                  </span>
                  <strong class="text-base">{anticipoAmount(mode)}</strong>
                  {#if anticipoDownloadMode === mode}
                    <span class="text-xs font-normal">{$t('invoices.anticipo.downloading')}</span>
                  {/if}
                </button>
              {/each}

              {#if anticipoData.splitPayment}
                <button
                  class="btn btn-secondary min-h-20 flex flex-col items-start justify-center gap-1 text-left"
                  on:click={() => downloadAnticipoFattura('net')}
                  disabled={anticipoDownloadMode !== null}
                  aria-label="{$t('invoices.anticipo.options.net')}: {anticipoAmount('net')}"
                >
                  <span class="inline-flex items-center gap-2">
                    {#if anticipoDownloadMode === 'net'}
                      <RefreshCw size={14} class="animate-spin" />
                    {:else}
                      <Download size={14} />
                    {/if}
                    {$t('invoices.anticipo.options.net')}
                  </span>
                  <strong class="text-base">{anticipoAmount('net')}</strong>
                  {#if anticipoDownloadMode === 'net'}
                    <span class="text-xs font-normal">{$t('invoices.anticipo.downloading')}</span>
                  {/if}
                </button>
              {/if}
            </div>

            {#if anticipoDownloadError}
              <div class="mt-4">
                <ErrorBanner error={anticipoDownloadError} title={$t('invoices.anticipo.downloadErrorTitle')} />
              </div>
            {/if}
          {/if}
        </section>
      </details>
    {:else if anticipoViewNow === 'retry'}
      <!-- The capabilities could not be read, so whether this account has the
           Anticipo section is not known: say so and offer to ask again, rather
           than leave a holder without it and without a word. -->
      <div class="alert alert-warning" role="alert" data-testid="anticipo-retry">
        <p class="alert-message">{$t('features.inline.anticipoLoadError')}</p>
        <button type="button" class="btn btn-secondary btn-sm mt-2" on:click={refreshCapabilities}>
          {$t('features.gate.retry')}
        </button>
      </div>
    {/if}

    <!-- A self-invoice is never money: no instalments — see showsInstalments().
         Open by default while something is still to be paid. -->
    {#if showsInstalments(invoice)}
      <details class="disclosure" open={isPaymentOpen(pagamento)}>
        <summary>
          <span class="disclosure-label"><span>{$t('invoices.tabs.tranches')}</span>{#if tranches.length > 0}<span class="tab-count"><span class="sr-only">, </span>{tranches.length}</span>{/if}</span>
        </summary>
        <div class="disclosure-content">
      <div class="space-y-4">
        <!-- Summary card + actions -->
        <div>
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div class="flex-1">
                            <p class="text-xs text-muted">{$t('invoices.tranches.description')}</p>
            </div>
            <!-- Writing instalments is for the roles that can write; the
                 others read the plan and its state. -->
            {#if canWrite}
            <div class="flex flex-wrap gap-2">
              {#if invoice.direction === 'inbound'}
                <button class="btn btn-secondary btn-sm" on:click={importTranchesFromXml} disabled={importingTranches}
                  title={$t('invoices.tranches.importFromXmlHint')}>
                  <Download size={14} class="mr-1 {importingTranches ? 'animate-pulse' : ''}" />
                  {$t('invoices.tranches.importFromXml')}
                </button>
              {/if}
              <button class="btn btn-accent btn-sm" on:click={openAddTrancheForm}>
                <Plus size={14} class="mr-1" aria-hidden="true" /> {$t('invoices.tranches.add')}
              </button>
            </div>
            {/if}
          </div>

          {#if tranches.length > 0}
            <dl class="summary-strip mt-4">
              <div class="summary-item">
                <dt class="summary-label">{$t('invoices.tranches.summary.count')}</dt>
                <dd class="summary-value">{tranches.length}</dd>
              </div>
              <div class="summary-item">
                <dt class="summary-label">{$t('invoices.tranches.summary.totalPlanned')}</dt>
                <dd class="summary-value">{formatEuro(tranchesPlannedTotal)}</dd>
              </div>
              <div class="summary-item">
                <dt class="summary-label">{$t('invoices.tranches.summary.totalPaid')}</dt>
                <dd class="summary-value text-success">{formatEuro(tranchesPaidTotal)}</dd>
              </div>
              <div class="summary-item">
                <dt class="summary-label">{$t('invoices.tranches.summary.outstanding')}</dt>
                <dd class="summary-value" class:text-accent={tranchesOutstanding > 0.01}>{formatEuro(tranchesOutstanding)}</dd>
              </div>
            </dl>

            {#if trancheTotalsMismatch}
              <p class="mt-4 text-xs text-warning">
                {$t('invoices.tranches.summary.mismatchWarning', { tranchesTotal: formatEuro(tranchesPlannedTotal), invoiceTotal: formatEuro(invoiceTotalNum) })}
              </p>
            {/if}
          {/if}
        </div>

        <ErrorBanner error={trancheError} />

        <!-- Add/edit form -->
        {#if showTrancheForm && canWrite}
          <div class="card p-5">
            <div class="flex items-center justify-between mb-4">
              <h4 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">
                {editingTrancheId ? $t('invoices.tranches.form.editTitle') : $t('invoices.tranches.form.title')}
              </h4>
              <button class="icon-button" on:click={cancelTrancheForm} title={$t('invoices.tranches.cancel')} aria-label={$t('invoices.tranches.cancel')}>
                <XIcon size={18} />
              </button>
            </div>
            <form on:submit|preventDefault={submitTrancheForm} class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.numero')}</span>
                <input class="input" type="number" min="1" bind:value={trancheForm.numeroTranche} />
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.dataScadenza')}</span>
                <input class="input" type="date" bind:value={trancheForm.dataScadenza} />
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.importo')} *</span>
                <input class="input" type="number" step="0.01" min="0" required bind:value={trancheForm.importo} />
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.modalita')}</span>
                <input class="input" maxlength="8" placeholder={$t('invoices.tranches.form.modalitaPlaceholder')} bind:value={trancheForm.modalitaPagamento} />
              </label>
              <label class="flex flex-col md:col-span-2">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.iban')}</span>
                <input class="input font-mono" maxlength="34" placeholder={$t('invoices.tranches.form.ibanPlaceholder')} bind:value={trancheForm.iban} />
              </label>
              <label class="flex flex-col md:col-span-2">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.note')}</span>
                <textarea class="input" rows="2" placeholder={$t('invoices.tranches.form.notePlaceholder')} bind:value={trancheForm.note}></textarea>
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.dataPagamento')}</span>
                <input class="input" type="date" bind:value={trancheForm.dataPagamento} />
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.importoPagato')}</span>
                <input class="input" type="number" step="0.01" min="0" bind:value={trancheForm.importoPagato} />
              </label>
              <label class="flex flex-col">
                <span class="eyebrow text-muted mb-1">{$t('invoices.tranches.fields.metodoEffettivo')}<InfoHint term="metodoEffettivo" /></span>
                <select class="input" bind:value={trancheForm.metodoEffettivo}>
                  <option value="">{$t('invoices.pagamento.metodo.none')}</option>
                  {#each METODI_EFFETTIVI as m}
                    <option value={m}>{$t(metodoEffettivoLabel(m))}</option>
                  {/each}
                </select>
              </label>
              <div class="md:col-span-2 flex justify-end gap-2">
                <button type="button" class="btn btn-secondary btn-sm" on:click={cancelTrancheForm}>{$t('invoices.tranches.cancel')}</button>
                <button type="submit" class="btn btn-accent btn-sm"><Save size={14} class="mr-1" /> {$t('invoices.tranches.save')}</button>
              </div>
            </form>
          </div>
        {/if}

        <!-- Tranches table -->
        <div>
          {#if tranches.length === 0}
            <div class="p-8 text-center text-muted text-sm">
              {invoice.direction === 'inbound' ? $t('invoices.tranches.emptyInbound') : $t('invoices.tranches.emptyOutbound')}
            </div>
          {:else}
            <div class="overflow-x-auto">
              <table class="table-modern table-stack" role="table">
                <thead class="table-header" role="rowgroup">
                  <tr role="row">
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.numero')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.dataScadenza')}</th>
                    <th class="table-cell table-header-cell text-right" role="columnheader">{$t('invoices.tranches.fields.importo')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.modalita')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.fields.status')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.dataPagamento')}</th>
                    <th class="table-cell table-header-cell text-right" role="columnheader">{$t('invoices.tranches.fields.importoPagato')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.metodoEffettivo')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.tranches.fields.source')}</th>
                    <th class="table-cell table-header-cell text-right" role="columnheader"><span class="sr-only">{$t('invoices.tranches.actions')}</span></th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {#each tranches as tr}
                    {@const status = trancheStatusInfo(tr)}
                    {@const actions = instalmentActions(tr)}
                    <tr class="table-row" role="row" style="cursor: default;">
                      <td role="cell" data-label={$t('invoices.tranches.fields.numero')} class="table-cell font-mono text-xs">#{tr.numeroTranche}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.dataScadenza')} class="table-cell text-xs font-mono">{formatShortDate(tr.dataScadenza)}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.importo')} class="table-cell font-mono text-right font-bold text-heading">{formatEuro(tr.importo)}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.modalita')} class="table-cell text-xs font-mono">{tr.modalitaPagamento || '-'}</td>
                      <td role="cell" data-label={$t('invoices.fields.status')} class="table-cell"><span class="badge {status.className}">{status.label}</span></td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.dataPagamento')} class="table-cell text-xs font-mono">{formatShortDate(tr.dataPagamento)}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.importoPagato')} class="table-cell font-mono text-right">{tr.dataPagamento ? formatEuro(tr.importoPagato ?? tr.importo) : '-'}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.metodoEffettivo')} class="table-cell text-xs">{tr.metodoEffettivo ? $t(metodoEffettivoLabel(tr.metodoEffettivo)) : '-'}</td>
                      <td role="cell" data-label={$t('invoices.tranches.fields.source')} class="table-cell text-xs">
                        <span class="badge badge-gray">{trancheSourceLabel(tr.source)}</span>
                      </td>
                      <td role="cell" data-label="" class="table-cell text-right">
                        {#if canWrite}
                        <div class="flex items-center gap-1 justify-end">
                          {#if actions.markPaid}
                            <button class="icon-button hover:text-success" title={$t('invoices.tranches.markPaid')} aria-label={$t('invoices.tranches.markPaid')} on:click={() => markTranchePaid(tr)}>
                              <CheckCircle size={16} />
                            </button>
                          {/if}
                          {#if actions.markUnpaid}
                            <button class="icon-button hover:text-warning" title={$t('invoices.tranches.markUnpaid')} aria-label={$t('invoices.tranches.markUnpaid')} on:click={() => markTrancheUnpaid(tr)}>
                              <RefreshCw size={16} />
                            </button>
                          {/if}
                          <button class="icon-button" title={$t('invoices.tranches.edit')} aria-label={$t('invoices.tranches.edit')} on:click={() => openEditTrancheForm(tr)}>
                            <Pencil size={16} />
                          </button>
                          <button class="icon-button hover:text-danger" title={$t('invoices.tranches.confirmDelete')} aria-label={$t('invoices.tranches.confirmDelete')} on:click={() => deleteTranche(tr)}>
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {/if}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      </div>

        </div>
      </details>
    {/if}

    <details class="disclosure">
      <summary>
        <span class="disclosure-label"><span>{$t('invoices.tabs.history')}</span>{#if history.length > 0}<span class="tab-count"><span class="sr-only">, </span>{history.length}</span>{/if}</span>
      </summary>
      <div class="disclosure-content">
        {#if history.length === 0}
          <p class="text-sm text-muted">{$t('invoices.history.empty')}</p>
        {:else}
          <ol class="timeline">
            {#each history as h}
              <li class="timeline-item">
                <span class="timeline-dot" class:timeline-dot-danger={h.result !== 'success'} aria-hidden="true"></span>
                <div class="timeline-text">
                  <strong>
                    {h.fromStatus ? statusLabel(h.fromStatus) : '—'} → {statusLabel(h.toStatus)}
                    <span class="sr-only">— {h.result === 'success' ? $t('invoices.history.success') : $t('invoices.history.failure')}</span>
                  </strong>
                  <span>{formatDate(h.createdAt)}{#if h.trigger} · {h.trigger}{/if}</span>
                </div>
              </li>
            {/each}
          </ol>
        {/if}
      </div>
    </details>

    <!-- Transport diagnostics are for administrators only. -->
    {#if $isAdmin}
      <details class="disclosure">
        <summary>
          <span class="disclosure-label"><span>{$t('invoices.tabs.transmissions')}</span>{#if transmissions.length > 0}<span class="tab-count"><span class="sr-only">, </span>{transmissions.length}</span>{/if}</span>
        </summary>
        <div class="disclosure-content">
      <div>
        {#if transmissions.length === 0}
          <div class="text-sm text-muted">{$t('invoices.transmissions.empty')}</div>
        {:else}
          <div class="overflow-x-auto">
            <table class="table-modern table-stack" role="table">
              <thead class="table-header" role="rowgroup">
                <tr role="row">
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.time')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.operation')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.status')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.sdiResponse')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.http')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.duration')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.transmissions.col.attempt')}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {#each transmissions as tx}
                  <tr class="table-row" role="row" style="cursor: default;">
                    <td role="cell" data-label={$t('invoices.transmissions.col.time')} class="table-cell text-xs">{formatDate(tx.createdAt)}</td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.operation')} class="table-cell font-bold">{tx.operation || '-'}</td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.status')} class="table-cell"><span class="badge {statusBadgeClass(tx.status)}">{tx.status}</span></td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.sdiResponse')} class="table-cell">{tx.sdiResponseCode || '-'}</td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.http')} class="table-cell">{tx.httpStatus || '-'}</td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.duration')} class="table-cell">{tx.durationMs ? `${tx.durationMs}ms` : '-'}</td>
                    <td role="cell" data-label={$t('invoices.transmissions.col.attempt')} class="table-cell">{tx.attempt || '-'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>

        </div>
      </details>
    {/if}

    <details class="disclosure">
      <summary>
        <span class="disclosure-label"><span>{$t('invoices.tabs.notifications')}</span>{#if notifications.length > 0}<span class="tab-count"><span class="sr-only">, </span>{notifications.length}</span>{/if}</span>
      </summary>
      <div class="disclosure-content">
      <div>
        {#if notifications.length === 0}
          <div class="text-sm text-muted">{$t('invoices.notifications.empty')}</div>
        {:else}
          <div class="overflow-x-auto">
            <table class="table-modern table-stack" role="table">
              <thead class="table-header" role="rowgroup">
                <tr role="row">
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.notifications.col.time')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">
                    <span class="inline-flex items-center gap-1">{$t('invoices.notifications.col.type')}<InfoHint term="ricevute" /></span>
                  </th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.notifications.col.sdiId')}</th>
                  <th class="table-cell table-header-cell text-left" role="columnheader">{$t('invoices.notifications.col.file')}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {#each notifications as n}
                  {@const nType = notificationType(n)}
                  {@const sdiCode = notificationSdiCode(nType)}
                  <tr class="table-row" role="row" style="cursor: default;">
                    <td role="cell" data-label={$t('invoices.notifications.col.time')} class="table-cell text-xs">{formatDate(n.createdAt)}</td>
                    <!-- The Italian name is the answer; the two-letter SdI code
                         next to it is what the Agenzia delle Entrate
                         documentation calls the same receipt. The raw
                         snake_case identifier the database stores is not shown
                         to anyone. -->
                    <td role="cell" data-label={$t('invoices.notifications.col.type')} class="table-cell font-bold">
                      {notificationTypeLabel(nType)}
                      {#if sdiCode}<span class="text-muted font-normal font-mono text-xs">({sdiCode})</span>{/if}
                    </td>
                    <td role="cell" data-label={$t('invoices.notifications.col.sdiId')} class="table-cell">{n.identificativoSdi || '-'}</td>
                    <td role="cell" data-label={$t('invoices.notifications.col.file')} class="table-cell text-xs break-all">{n.nomeFile || '-'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
      </div>
    </details>

    {#if transitions && $isAdmin}
      <details class="disclosure">
        <summary>{$t('invoices.sections.allowedTransitions')}</summary>
        <div class="disclosure-content">
            {#if transitions.allowedTransitions?.length > 0}
              <div class="flex flex-wrap gap-2">
                {#each transitions.allowedTransitions as trans}
                  <span class="badge badge-blue">{trans}</span>
                {/each}
              </div>
            {:else}
              <p class="text-muted text-sm">{$t('invoices.sections.noTransitions')}</p>
            {/if}
        </div>
      </details>
    {/if}

    {#if canWrite && showSignModal}
      <SignInvoiceModal
        companyId={$activeCompanyId}
        {invoiceId}
        status={signature}
        on:close={() => showSignModal = false}
        on:signed={async () => { await loadData(); }}
      />
    {/if}

    {#if canWrite && showEsitoModal}
      <SendEsitoModal
        companyId={$activeCompanyId}
        {invoiceId}
        identificativoSdi={invoice.identificativoSdi}
        on:close={() => showEsitoModal = false}
        on:sent={handleEsitoSent}
      />
    {/if}
    {#if canWrite && payingOpen}
      <RecordPaymentDialog companyId={$activeCompanyId} {invoice}
        on:recorded={onPaymentRecorded} on:refresh={onPaymentRecorded} on:cancel={() => (payingOpen = false)} />
    {/if}
  {:else}
    <!-- No company selected: a fattura always belongs to one, and without it
         there is nothing to ask the server for. -->
    <EmptyState icon={Building2} title={$t('invoices.noCompanyTitle')} body={$t('invoices.noCompanyBody')}>
      <button class="btn btn-secondary btn-sm" on:click={() => push('/invoices')}>{$t('invoices.backToList')}</button>
    </EmptyState>
  {/if}
</Layout>
